import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, BladApi, pobierzPlik } from '../api/client';
import type { Dokument, DokumentNaLiscie, Lista, TypDokumentu } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Miejsce, Plakietka, Pole, Przycisk, Pusto, Zakladki,
} from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { STATUS_DOKUMENTU, TYP_DOKUMENTU } from '../lib/slowniki';
import { data as fData, dataGodzina, ilosc, zlote } from '../lib/format';

export function Dokumenty() {
  const { maUprawnienie } = useAuth();
  const [parametry, ustawParametry] = useSearchParams();
  const [typ, setTyp] = useState<TypDokumentu | ''>('');
  const [szukaj, setSzukaj] = useState('');

  const otwarty = parametry.get('otwarty');

  const zapytanie = new URLSearchParams({
    ...(typ ? { type: typ } : {}),
    ...(szukaj ? { szukaj } : {}),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['dokumenty', zapytanie.toString()],
    queryFn: () => api.get<Lista<DokumentNaLiscie>>(`/dokumenty?${zapytanie}`),
    refetchInterval: 20_000,
  });

  function przelacz(id: number) {
    const nowe = new URLSearchParams(parametry);
    if (otwarty === String(id)) nowe.delete('otwarty');
    else nowe.set('otwarty', String(id));
    ustawParametry(nowe, { replace: true });
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Dokumenty magazynowe</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Każdy zatwierdzony dokument jest nieodwracalny — poprawia się go stornem, które zostawia ślad.
          </p>
        </div>
        <PrzyciskExcel dzial="dokumenty" parametry={zapytanie} className="ml-auto" />
        {maUprawnienie('ruch.przyjecie', 'ruch.wydanie', 'ruch.korekta', 'ruch.przesuniecie', 'ruch.likwidacja') && (
          <Link to="/dokumenty/nowy">
            <Przycisk wariant="glowny">Wystaw dokument</Przycisk>
          </Link>
        )}
      </div>

      <Zakladki
        wartosc={typ} zmien={setTyp}
        opcje={[
          { wartosc: '', etykieta: 'Wszystkie' },
          { wartosc: 'PZ', etykieta: 'Przyjęcia' },
          { wartosc: 'WZ', etykieta: 'Wydania' },
          { wartosc: 'KOR', etykieta: 'Korekty' },
          { wartosc: 'MM', etykieta: 'Przesunięcia' },
          { wartosc: 'LIK', etykieta: 'Likwidacje' },
        ]}
      />

      <div className="max-w-md">
        <Pole etykieta="Szukaj po numerze" value={szukaj}
          onChange={(e) => setSzukaj(e.target.value)} placeholder="np. WZ/2026/0012" />
      </div>

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <ul className="divide-y divide-kamien-200">
              {lista.data.pozycje.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => przelacz(d.id)}
                    className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-left hover:bg-kamien-50"
                  >
                    <span className="w-36 font-mono text-xs font-medium text-kamien-900">
                      {d.number ?? '(bez numeru)'}
                    </span>
                    <span className="w-28 text-sm text-kamien-700">{TYP_DOKUMENTU[d.type].nazwa}</span>
                    <span className="w-32 text-sm text-kamien-500">{fData(d.issuedAt)}</span>
                    <span className="flex-1 text-sm text-kamien-600">
                      {d.supplier?.name ?? d.class?.course.name ?? d.issuePurpose?.name ?? d.reason ?? '—'}
                    </span>
                    <span className="text-xs text-kamien-400">{d._count.movements} poz.</span>
                    <Plakietka ton={STATUS_DOKUMENTU[d.status].ton}>
                      {STATUS_DOKUMENTU[d.status].etykieta}
                    </Plakietka>
                  </button>
                  {otwarty === String(d.id) && <Szczegoly id={d.id} />}
                </li>
              ))}
            </ul>
          ) : (
            <Pusto
              tytul="Brak dokumentów"
              opis="Pierwszy dokument to zwykle przyjęcie PZ — od niego zaczyna się stan magazynu."
            />
          )}
        </Karta>
      )}
    </div>
  );
}

function Szczegoly({ id }: { id: number }) {
  const { maUprawnienie } = useAuth();
  const qc = useQueryClient();
  const widziCeny = maUprawnienie('ceny.odczyt');
  const mozeDrukowac = maUprawnienie('raporty.eksport');
  const [powod, setPowod] = useState('');
  const [pokazStorno, setPokazStorno] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [drukowanie, setDrukowanie] = useState(false);

  async function drukuj() {
    setBlad(null); setDrukowanie(true);
    try {
      await pobierzPlik(`/dokumenty/${id}/pdf`, `dokument-${id}.pdf`);
    } catch (e) {
      setBlad(e instanceof BladApi ? e.message : 'Nie udało się przygotować wydruku.');
    } finally {
      setDrukowanie(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['dokument', id],
    queryFn: () => api.get<Dokument>(`/dokumenty/${id}`),
  });

  const stornuj = useMutation({
    mutationFn: () => api.post(`/dokumenty/${id}/storno`, { powod }),
    onSuccess: () => {
      setPokazStorno(false); setPowod(''); setBlad(null);
      void qc.invalidateQueries({ queryKey: ['dokumenty'] });
      void qc.invalidateQueries({ queryKey: ['dokument', id] });
      void qc.invalidateQueries({ queryKey: ['stany'] });
    },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się wystornować.'),
  });

  if (isLoading || !data) return <div className="bg-kamien-50 px-5 py-4"><Ladowanie /></div>;

  const mozeStornowac = maUprawnienie('ruch.korekta') && data.status === 'ZATWIERDZONY';

  return (
    <div className="bg-kamien-50 px-5 py-4">
      <dl className="mb-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Para etykieta="Wystawił" wartosc={`${data.createdBy.fullName} (${data.createdBy.login})`} />
        <Para etykieta="Data" wartosc={dataGodzina(data.issuedAt)} />
        {data.supplier && <Para etykieta="Dostawca" wartosc={data.supplier.name} />}
        {data.issuePurpose && <Para etykieta="Cel wydania" wartosc={data.issuePurpose.name} />}
        {data.recipientNote && <Para etykieta="Odbiorca" wartosc={data.recipientNote} />}
        {data.relatedDocument && (
          <Para etykieta="Dotyczy" wartosc={data.relatedDocument.number ?? `#${data.relatedDocument.id}`} />
        )}
        {data.reason && <Para etykieta="Uzasadnienie" wartosc={data.reason} szerokie />}
        {data.notes && <Para etykieta="Uwagi" wartosc={data.notes} szerokie />}
      </dl>

      <div className="overflow-x-auto rounded border border-kamien-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
            <tr>
              <th className="px-4 py-2 font-medium">Lp.</th>
              <th className="px-4 py-2 font-medium">Pozycja</th>
              <th className="px-4 py-2 font-medium">Partia / egzemplarz</th>
              <th className="px-4 py-2 font-medium">Lokalizacja</th>
              <th className="px-4 py-2 text-right font-medium">Ilość</th>
              {widziCeny && <th className="px-4 py-2 text-right font-medium">Wartość</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-kamien-200">
            {data.movements.map((m) => (
              <tr key={m.id}>
                <td className="px-4 py-2 text-kamien-400">{m.lineNo}</td>
                <td className="px-4 py-2">
                  <span className="font-mono text-xs text-kamien-500">{m.item.code}</span>{' '}
                  <span className="text-kamien-900">{m.item.name}</span>
                  {m.item.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                  {m.expiredOverride && (
                    <span className="mt-1 block text-xs text-status-brak">
                      wydane po terminie — {m.expiredOverrideReason}
                    </span>
                  )}
                  {m.notes && <span className="mt-0.5 block text-xs text-kamien-500">{m.notes}</span>}
                </td>
                <td className="px-4 py-2 text-xs text-kamien-600">
                  {m.equipmentUnit
                    ? <span className="font-mono">{m.equipmentUnit.nrCsm}</span>
                    : m.batch
                      ? <>
                          <span className="font-mono">{m.batch.lotNumber ?? `partia ${m.batch.id}`}</span>
                          {m.batch.expiryDate && <span className="block">termin {fData(m.batch.expiryDate)}</span>}
                        </>
                      : '—'}
                </td>
                <td className="px-4 py-2 text-xs"><Miejsce lokalizacja={m.location} /></td>
                <td className={`px-4 py-2 text-right font-medium ${
                  Number(m.qty) < 0 ? 'text-status-brak' : 'text-status-ok'}`}>
                  {Number(m.qty) > 0 ? '+' : ''}{ilosc(m.qty)} {m.item.unit}
                </td>
                {widziCeny && (
                  <td className="px-4 py-2 text-right text-kamien-600">{zlote(m.value)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {blad && <div className="mt-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}

      <div className="mt-4 flex flex-wrap gap-2">
        {data.type === 'WZ' && data.status === 'ZATWIERDZONY' && (
          <Link to={`/rozliczenie/${data.id}`}>
            <Przycisk wariant="glowny" className="min-h-9 text-xs">Rozlicz zajęcia</Przycisk>
          </Link>
        )}
        {mozeDrukowac && (
          <Przycisk className="min-h-9 text-xs" onClick={() => void drukuj()} disabled={drukowanie}>
            {drukowanie ? 'Przygotowuję...' : 'Wydruk PDF do podpisu'}
          </Przycisk>
        )}
      </div>

      {mozeStornowac && (
        <div className="mt-4">
          {pokazStorno ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-72 flex-1">
                <Pole etykieta="Powód storna" value={powod} onChange={(e) => setPowod(e.target.value)}
                  podpowiedz="Trafi na dokument korekty i do dziennika" autoFocus />
              </div>
              <Przycisk wariant="ostrzegawczy" disabled={stornuj.isPending || powod.trim().length < 3}
                onClick={() => stornuj.mutate()}>
                {stornuj.isPending ? 'Stornowanie...' : 'Potwierdź storno'}
              </Przycisk>
              <Przycisk wariant="cichy" onClick={() => { setPokazStorno(false); setBlad(null); }}>
                Anuluj
              </Przycisk>
            </div>
          ) : (
            <Przycisk wariant="ostrzegawczy" onClick={() => setPokazStorno(true)}>
              Stornuj dokument
            </Przycisk>
          )}
        </div>
      )}

      {data.status === 'STORNOWANY' && (
        <p className="mt-3 text-sm text-status-brak">
          Ten dokument został wystornowany. Jego zapisy zostały w księdze — odwraca je osobny dokument korekty.
        </p>
      )}
    </div>
  );
}

function Para({ etykieta, wartosc, szerokie }: { etykieta: string; wartosc: string; szerokie?: boolean }) {
  return (
    <div className={szerokie ? 'sm:col-span-2 lg:col-span-4' : undefined}>
      <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className="text-kamien-800">{wartosc}</dd>
    </div>
  );
}
